

import { useState, useEffect, useMemo } from "react";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Calendar,
  Users,
  Building2,
  BedDouble,
  ChevronUp,
  ChevronDown,
  Clock,
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import { mastersApi, eventsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { formatCurrency, formatDate } from "@/lib/data";

interface Room {
  id: number;
  name: string;
  type: string;
  capacity: number;
  rate: number;
  status: string;
}

interface Event {
  id: number;
  name: string;
  client?: { name: string };
}

interface RoomBooking {
  id: number;
  event_id: number;
  room_id: number | null;
  guest_name: string;
  guest_count: number;
  check_in: string;
  check_out: string;
  rate: number;
  notes: string | null;
  status: string;
  event?: Event;
  room?: Room;
}

function StatBadge({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div className={`px-3 py-1.5 rounded-lg ${color} flex items-center gap-2`}>
      <span className="text-xs font-medium opacity-80">{label}:</span>
      <span className="text-sm font-bold">{value}</span>
    </div>
  );
}

function RoomBookingForm({
  initialData,
  rooms,
  events,
  onSubmit,
  onCancel,
}: {
  initialData?: Partial<RoomBooking>;
  rooms: Room[];
  events: Event[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
}) {
  const [event_id, setEventId] = useState(initialData?.event_id?.toString() || "");
  const [room_id, setRoomId] = useState(initialData?.room_id?.toString() || "");
  const [guest_name, setGuestName] = useState(initialData?.guest_name || "");
  const [guest_count, setGuestCount] = useState(initialData?.guest_count?.toString() || "1");
  const [check_in, setCheckIn] = useState(() => {
    if (initialData?.check_in) { const d = new Date(initialData.check_in); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [check_out, setCheckOut] = useState(() => {
    if (!initialData?.check_out) return "";
    const d = new Date(initialData.check_out); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [rate, setRate] = useState(initialData?.rate?.toString() || "");
  const [notes, setNotes] = useState(initialData?.notes || "");

  // Auto-fill rate when room is selected
  const handleRoomChange = (roomId: string) => {
    setRoomId(roomId);
    const selectedRoom = rooms.find(r => r.id.toString() === roomId);
    if (selectedRoom && !initialData?.rate) {
      setRate(selectedRoom.rate.toString());
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      event_id: Number(event_id),
      room_id: room_id ? Number(room_id) : null,
      guest_name,
      guest_count: Number(guest_count),
      check_in,
      check_out,
      rate: Number(rate),
      notes: notes || null,
    });
  };

  const inputClass = "w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none";

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Event *</label>
        <select
          value={event_id}
          onChange={(e) => setEventId(e.target.value)}
          className={inputClass}
          required
        >
          <option value="">Select event</option>
          {events.map((event) => (
            <option key={event.id} value={event.id}>
              {event.name} {event.client ? `- ${event.client.name}` : ''}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Room</label>
          <select
            value={room_id}
            onChange={(e) => handleRoomChange(e.target.value)}
            className={inputClass}
          >
            <option value="">Select room</option>
            {rooms.filter(r => r.status === 'available').map((room) => (
              <option key={room.id} value={room.id}>
                {room.name} ({room.type})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Rate per Night (₹) *</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder="0.00"
            className={inputClass}
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Guest Name *</label>
        <input
          type="text"
          value={guest_name}
          onChange={(e) => setGuestName(e.target.value)}
          placeholder="e.g., John Doe"
          className={inputClass}
          required
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Number of Guests *</label>
        <input
          type="number"
          min="1"
          value={guest_count}
          onChange={(e) => setGuestCount(e.target.value)}
          className={inputClass}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Check-in Date *</label>
          <input
            type="date"
            value={check_in}
            onChange={(e) => setCheckIn(e.target.value)}
            className={inputClass}
            required
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Check-out Date *</label>
          <input
            type="date"
            value={check_out}
            onChange={(e) => setCheckOut(e.target.value)}
            min={check_in}
            className={inputClass}
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Special requests, preferences..."
          rows={3}
          className={`${inputClass} resize-none`}
        />
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData?.id ? "Update" : "Add"} Booking
        </button>
      </div>
    </form>
  );
}

export default function RoomBookingsPage() {
  const { showToast } = useToast();

  const [bookings, setBookings] = useState<RoomBooking[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState<RoomBooking | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingBooking, setDeletingBooking] = useState<RoomBooking | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [bookingsRes, roomsRes, eventsRes] = await Promise.all([
        mastersApi.roomBookings.list(),
        mastersApi.rooms.list(),
        eventsApi.list(),
      ]);
      
      const bookingsData = bookingsRes.data?.data?.data || bookingsRes.data?.data || [];
      setBookings(bookingsData);
      
      const roomsData = roomsRes.data?.data || [];
      setRooms(roomsData);
      
      const eventsData = eventsRes.data?.data?.data || eventsRes.data?.data || [];
      setEvents(eventsData);
    } catch (error) {
      showToast("Failed to load room bookings", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredBookings = useMemo(() => {
    if (!searchQuery) return bookings;
    const q = searchQuery.toLowerCase();
    return bookings.filter(
      (b) =>
        b.guest_name.toLowerCase().includes(q) ||
        b.event?.name?.toLowerCase().includes(q) ||
        b.room?.name?.toLowerCase().includes(q)
    );
  }, [bookings, searchQuery]);

  const stats = useMemo(
    () => ({
      total: bookings.length,
      today: bookings.filter((b) => {
        const d = new Date(); const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        return b.check_in <= today && b.check_out >= today;
      }).length,
      upcoming: bookings.filter((b) => { const d = new Date(); return b.check_in > `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }).length,
    }),
    [bookings]
  );

  const calculateNights = (checkIn: string, checkOut: string) => {
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diffTime = end.getTime() - start.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const handleDelete = async () => {
    if (!deletingBooking) return;
    setDeleteLoading(true);
    try {
      await mastersApi.roomBookings.delete(deletingBooking.id);
      setBookings((p) => p.filter((b) => b.id !== deletingBooking.id));
      showToast("Room booking deleted successfully", "success");
      setDeleteModalOpen(false);
      setDeletingBooking(null);
    } catch (error) {
      showToast("Failed to delete room booking", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSubmit = async (data: any) => {
    setIsModalOpen(false);
    setEditingBooking(null);
    try {
      if (editingBooking) {
        await mastersApi.roomBookings.update(editingBooking.id, data);
        showToast("Room booking updated successfully", "success");
        loadData();
      } else {
        await mastersApi.roomBookings.create(data);
        showToast("Room booking created successfully", "success");
        loadData();
      }
    } catch (error) {
      showToast("Failed to save room booking", "error");
    }
  };


  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mb-4">
        <h1 className="text-xl font-bold text-stone-800">Room Bookings</h1>
        <p className="text-sm text-stone-500">Manage room reservations for events.</p>
      </div>

      {/* Compact Stats */}
      <div className="flex flex-wrap gap-2 mb-4">
        <StatBadge label="Total" value={stats.total} color="bg-stone-100 text-stone-700" />
        <StatBadge label="Today" value={stats.today} color="bg-blue-100 text-blue-700" />
        <StatBadge label="Upcoming" value={stats.upcoming} color="bg-emerald-100 text-emerald-700" />
      </div>

      {/* Search and Add */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search bookings..."
            className="w-full pl-9 pr-4 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          />
        </div>
        <button
          onClick={() => {
            setEditingBooking(null);
            setIsModalOpen(true);
          }}
          className="px-4 py-2 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Booking
        </button>
      </div>

      {filteredBookings.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <BedDouble className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No room bookings found</p>
        </div>
      ) : (
        <>
          {/* Mobile Card Layout */}
          <div className="sm:hidden space-y-2">
            {filteredBookings.map((booking) => (
              <div key={booking.id} className="bg-white rounded-xl border border-stone-200 p-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <BedDouble className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-medium text-stone-800 text-sm truncate">{booking.guest_name}</h3>
                      <span className="text-xs font-semibold text-amber-700">{formatCurrency(booking.rate)}/night</span>
                    </div>
                    <div className="text-xs text-stone-500 mb-1">
                      {booking.event?.name || 'No Event'}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-stone-400 mb-2">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(booking.check_in)} - {formatDate(booking.check_out)}
                      </span>
                      <span>({calculateNights(booking.check_in, booking.check_out)} nights)</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">
                        {booking.room?.name || 'No Room Assigned'}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => { setEditingBooking(booking); setIsModalOpen(true); }}
                          className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setDeletingBooking(booking); setDeleteModalOpen(true); }}
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table Layout */}
          <div className="hidden sm:block bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500">Guest</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500">Event</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500">Room</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-stone-500">Dates</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-stone-500">Guests</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-stone-500">Rate</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-stone-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filteredBookings.map((booking) => (
                    <tr key={booking.id} className="hover:bg-stone-50 transition-colors">
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <Users className="w-4 h-4 text-blue-600" />
                          </div>
                          <span className="font-medium text-stone-800 text-sm">{booking.guest_name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-sm text-stone-600">{booking.event?.name || '-'}</span>
                        {booking.event?.client && (
                          <p className="text-xs text-stone-400">{booking.event.client.name}</p>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">
                          {booking.room?.name || 'Not Assigned'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="text-sm text-stone-600">
                          {formatDate(booking.check_in)} - {formatDate(booking.check_out)}
                        </div>
                        <div className="text-xs text-stone-400">
                          {calculateNights(booking.check_in, booking.check_out)} nights
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center text-sm text-stone-600">
                        {booking.guest_count}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="text-sm font-semibold text-stone-800">{formatCurrency(booking.rate)}</div>
                        <div className="text-xs text-stone-400">per night</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => { setEditingBooking(booking); setIsModalOpen(true); }}
                            className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => { setDeletingBooking(booking); setDeleteModalOpen(true); }}
                            className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingBooking(null);
        }}
        title={editingBooking ? "Edit Room Booking" : "Add New Room Booking"}
        size="md"
      >
        <RoomBookingForm
          initialData={editingBooking || undefined}
          rooms={rooms}
          events={events}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingBooking(null);
          }}
        />
      </Modal>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeletingBooking(null);
        }}
        onConfirm={handleDelete}
        title="Delete Room Booking"
        message="Are you sure you want to delete this room booking? This action cannot be undone."
        itemName={deletingBooking?.guest_name}
        loading={deleteLoading}
      />
    </DashboardLayout>
  );
}
